export type UserFacingErrorKind =
  | 'auth'
  | 'login'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limit'
  | 'server'
  | 'network'
  | 'technical';

export interface UserFacingError {
  kind: UserFacingErrorKind;
  status: number | null;
  userMessage: string;
  needsRelogin: boolean;
  shouldRedirectToLogin: boolean;
  debugTraceId: string | null;
  rawDetail: string | null;
}

export interface UserFacingErrorOptions {
  context?: 'login' | 'action' | 'message-reply';
  fallbackMessage?: string;
  validationMessage?: string;
  conflictMessage?: string;
  notFoundMessage?: string;
  forbiddenMessage?: string;
}

const AUTH_DETAIL_PATTERNS = [
  'token expired',
  'jwt_expired',
  'unauthorized',
  'invalid token',
  'invalid authorization header',
  'authentication failed',
];

const TECHNICAL_DETAIL_PATTERNS = [
  'traceback',
  'httpexception',
  'expiredsignatureerror',
  'jwt_expired',
  'token expired',
  'unauthorized',
  'invalid token',
  'internal server error',
];

const ACTIONABLE_CONFLICT_CODES = new Set([
  'ARCHIVE_EXPORT_FILTER_UNSUPPORTED',
  'ARCHIVE_TICKET_LINKAGE_UNAVAILABLE',
]);

export function getUserFacingErrorStatus(error: unknown): number | null {
  const raw = (error as { response?: { status?: unknown } })?.response?.status;
  const status = Number(raw);
  return Number.isFinite(status) && status > 0 ? status : null;
}

export function getUserFacingErrorTraceId(error: unknown): string | null {
  const headers = (error as { response?: { headers?: Record<string, unknown> } })?.response?.headers;
  if (!headers || typeof headers !== 'object') return null;
  const traceId = headers['x-trace-id'] ?? headers['X-Trace-Id'];
  return typeof traceId === 'string' && traceId.trim() ? traceId.trim() : null;
}

export function extractUserFacingErrorDetail(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown; errors?: Array<{ message?: unknown; msg?: unknown }> } } })?.response?.data;
  if (typeof detail?.detail === 'string' && detail.detail.trim()) return detail.detail.trim();
  if (detail?.detail && typeof detail.detail === 'object') {
    const structured = detail.detail as { message?: unknown; detail?: unknown };
    if (typeof structured.message === 'string' && structured.message.trim()) return structured.message.trim();
    if (typeof structured.detail === 'string' && structured.detail.trim()) return structured.detail.trim();
  }
  if (Array.isArray(detail?.errors) && detail.errors.length > 0) {
    const first = detail.errors[0];
    if (typeof first?.message === 'string' && first.message.trim()) return first.message.trim();
    if (typeof first?.msg === 'string' && first.msg.trim()) return first.msg.trim();
  }
  const messageText = (error as { message?: unknown })?.message;
  return typeof messageText === 'string' && messageText.trim() ? messageText.trim() : null;
}

function extractStructuredErrorCode(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const structured = detail as { code?: unknown; error?: unknown };
  const value = typeof structured.code === 'string' ? structured.code : structured.error;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isNetworkError(error: unknown): boolean {
  const code = String((error as { code?: unknown })?.code ?? '').trim().toUpperCase();
  const messageText = String((error as { message?: unknown })?.message ?? '').trim().toLowerCase();
  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    messageText.includes('network error') ||
    messageText.includes('timeout') ||
    messageText.includes('failed to fetch')
  );
}

function isTechnicalDetail(detail: string | null): boolean {
  const normalized = String(detail ?? '').trim().toLowerCase();
  if (!normalized) return false;
  if (TECHNICAL_DETAIL_PATTERNS.some((pattern) => normalized.includes(pattern))) return true;
  return normalized.includes('_') && !normalized.includes(' ');
}

function isAuthDetail(detail: string | null): boolean {
  const normalized = String(detail ?? '').trim().toLowerCase();
  if (!normalized) return false;
  return AUTH_DETAIL_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function buildFallbackMessage(fallback: string | undefined, technical: string): string {
  return fallback && fallback.trim() ? fallback.trim() : technical;
}

export function interpretUserFacingError(error: unknown, options: UserFacingErrorOptions = {}): UserFacingError {
  const status = getUserFacingErrorStatus(error);
  const detail = extractUserFacingErrorDetail(error);
  const traceId = getUserFacingErrorTraceId(error);
  const fallbackMessage = buildFallbackMessage(options.fallbackMessage, 'Не удалось выполнить действие. Попробуйте ещё раз.');
  const loginContext = options.context === 'login';

  if (isNetworkError(error) || status == null) {
    return {
      kind: 'network',
      status,
      userMessage: options.context === 'message-reply'
        ? 'Исход доставки неизвестен. Не повторяйте отправку до проверки её результата; проверьте состояние ответа.'
        : 'Не удалось связаться с сервером. Проверьте интернет-соединение и попробуйте снова.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (loginContext && (status === 401 || status === 403)) {
    return {
      kind: 'login',
      status,
      userMessage: 'Не удалось войти. Проверьте логин и пароль и попробуйте снова.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status === 401 || isAuthDetail(detail)) {
    return {
      kind: 'auth',
      status,
      userMessage: 'Сессия истекла. Пожалуйста, войдите в систему заново.',
      needsRelogin: true,
      shouldRedirectToLogin: true,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  const replyCode = extractStructuredErrorCode(error);
  const replyMessages: Record<string, string> = {
    reply_scope_denied: 'У вас нет права ответить на это сообщение в текущей области доступа.',
    reply_message_unavailable: 'Сообщение недоступно в текущей области доступа.',
    reply_target_unavailable: 'Не удалось подтвердить исходное сообщение и маршрут ответа. Отправка сейчас недоступна.',
    reply_native_parent_missing: 'Telegram не нашёл исходное сообщение для ответа. Отправка остановлена.',
    reply_recipient_blocked: 'Получатель заблокировал бота. Ответ не доставлен.',
    reply_delivery_temporary: 'Доставка временно задержана. Проверьте состояние ответа.',
    reply_delivery_unknown: 'Исход доставки неизвестен. Не повторяйте отправку до проверки её результата; проверьте состояние ответа.',
  };
  if (replyCode && replyMessages[replyCode]) {
    return { kind: replyCode === 'reply_scope_denied' ? 'forbidden' : status === 404 ? 'not_found' : 'conflict',
      status, userMessage: replyMessages[replyCode], needsRelogin: false,
      shouldRedirectToLogin: false, debugTraceId: traceId, rawDetail: detail };
  }

  if (status === 403) {
    return {
      kind: 'forbidden',
      status,
      userMessage: options.forbiddenMessage?.trim() || 'У вас нет доступа к этому действию.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status === 404) {
    return {
      kind: 'not_found',
      status,
      userMessage: options.notFoundMessage?.trim() || (options.context === 'message-reply'
        ? 'Сообщение или маршрут ответа сейчас недоступны. Исход доставки проверьте в состоянии ответов.'
        : 'Нужные данные не найдены или были удалены.'),
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status === 409) {
    const conflictCode = extractStructuredErrorCode(error);
    const actionableDetail = conflictCode && ACTIONABLE_CONFLICT_CODES.has(conflictCode) && detail && !isTechnicalDetail(detail)
      ? detail
      : null;
    return {
      kind: 'conflict',
      status,
      userMessage: options.conflictMessage?.trim()
        || actionableDetail
        || 'Данные изменились. Пожалуйста, обновите страницу и повторите действие.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status === 422) {
    return {
      kind: 'validation',
      status,
      userMessage:
        detail && !isTechnicalDetail(detail)
          ? detail
          : options.validationMessage?.trim() || 'Проверьте введённые данные и попробуйте снова.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status === 429) {
    return {
      kind: 'rate_limit',
      status,
      userMessage: 'Слишком много запросов. Попробуйте чуть позже.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  if (status >= 500) {
    return {
      kind: 'server',
      status,
      userMessage: 'На сервере возникла ошибка. Попробуйте ещё раз немного позже.',
      needsRelogin: false,
      shouldRedirectToLogin: false,
      debugTraceId: traceId,
      rawDetail: detail,
    };
  }

  return {
    kind: 'technical',
    status,
    userMessage: fallbackMessage,
    needsRelogin: false,
    shouldRedirectToLogin: false,
    debugTraceId: traceId,
    rawDetail: detail,
  };
}

export function getUserFacingErrorMessage(error: unknown, options: UserFacingErrorOptions = {}): string {
  return interpretUserFacingError(error, options).userMessage;
}
